import type { HTMLAttributes } from 'react';
import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { useRefs } from '@/scripts/hooks';
import classes from './index.module.scss';

interface IntersectingProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  chat: Chat;
  message: ChatMessage;
  shouldObserver?: boolean;
  onMessageShow?: (message: ChatMessage) => void;
  onMessageHidden?: (message: ChatMessage) => void;
}

const ObserverItem = ({
  chat,
  message,
  shouldObserver = true,
  onMessageShow,
  onMessageHidden,
  children,
  ...props
}: IntersectingProps) => {
  const observerItemRef = useRef<HTMLDivElement>(null);

  const refs = useRefs({
    chat,
    message,
    onMessageShow,
    onMessageHidden,
  });

  useEffect(() => {
    if (!shouldObserver) return;
    const { message, onMessageShow, onMessageHidden } = refs.current;
    const observer_ = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting) {
          onMessageShow?.(message);
        } else {
          onMessageHidden?.(message);
        }
      },
      { threshold: 0.02 }
    );
    if (observerItemRef.current) {
      observer_.observe(observerItemRef.current);
    }
    return () => {
      observer_.disconnect();
    };
  }, [refs, chat.chat_id, message.status, shouldObserver]);

  return (
    <div {...props} ref={observerItemRef} className={cls(classes.messageItem)}>
      {children}
    </div>
  );
};

export default ObserverItem;
